import { getUserAccount } from '@/configuration/login/loginSlice';
import { resolveDocumentViewerLayout } from '@/utils/documentViewerLayout/documentViewerLayout';
import { measureDocumentViewerSpace } from '@/utils/documentViewerLayout/measureSpace';

import { selectCurrentLayout } from '../layout/layoutSlice';
import type { LayoutItem } from '../layout/sidebarToContent';
import { startAppListening } from '../middleware/listenerMiddleware';
import { selectIsOnboardingMode } from '../onboarding/onboardingSlice';
import { closeSidebar, selectIsSidebarOpen } from '../sidebar/sidebarSlice';
import type { RootState } from '../store';
import { selectActiveThreadId } from '../thread/threadSlice';
import { closeDocumentViewer, openDocument, selectIsDocumentViewerOpen } from './documentViewerSlice';

const CONVERSATION_LAYOUTS: readonly LayoutItem[] = ['chat', 'onboardingMode'];

/**
 * The conversation the open Document belongs to (thread, fleet, and wheteher it is active are not).
 */
const toConversationIdentity = (state: RootState) => ({
  activeThreadId: selectActiveThreadId(state),
  fleetAccount: getUserAccount(state),
  isOnboardingMode: selectIsOnboardingMode(state),
});

/**
 * Closes the document viewer when the conversation it belongs to is no longer the activeThreadId
 */
startAppListening({
  predicate: (_action, currentState, previousState) => {
    if (!selectIsDocumentViewerOpen(currentState)) return false;

    if (!CONVERSATION_LAYOUTS.includes(selectCurrentLayout(currentState))) return true;

    const current = toConversationIdentity(currentState);
    const previous = toConversationIdentity(previousState);

    return (
      current.activeThreadId !== previous.activeThreadId ||
      current.fleetAccount !== previous.fleetAccount ||
      current.isOnboardingMode !== previous.isOnboardingMode
    );
  },
  effect: (_action, { dispatch }) => {
    dispatch(closeDocumentViewer());
  },
});

/**
 * Collapses the thread history when a Document opens and the Viewer would not fit beside it, so the conversation
 * keeps its width. The panel opened last takes the room.
 */
startAppListening({
  actionCreator: openDocument,
  effect: (_action, { dispatch, getState }) => {
    if (!selectIsSidebarOpen(getState())) return;
    if (resolveDocumentViewerLayout(measureDocumentViewerSpace()).fits) return;

    dispatch(closeSidebar());
  },
});
